import type {
  ThreeDViewerPartSearchResult,
  ThreeDViewerPartSearchSource
} from '@/interfaces/library'

function getPartKey ({ part }: ThreeDViewerPartSearchSource) {
  return part.id != null
    ? `id:${part.id}`
    : `part:${part.supplierKey ?? ''}:${part.partNumber ?? ''}`
}

function getTagValues (tags: ThreeDViewerPartSearchSource['part']['tags']) {
  if (Array.isArray(tags)) return tags.map(({ value }) => value)
  if (!tags) return []

  const nestedTagValues = tags.tagValues
  if (Array.isArray(nestedTagValues)) {
    return nestedTagValues.flatMap((tag) => {
      if (!tag || typeof tag !== 'object') return []
      const value = (tag as { value?: unknown }).value
      return typeof value === 'string' ? [value] : []
    })
  }

  if (typeof tags.value === 'string') return [tags.value]
  return Object.values(tags).filter((value): value is string => typeof value === 'string')
}

function getSearchTerm (query: string) {
  const term = query.trim()
  if (term.length >= 2 && term.startsWith('"') && term.endsWith('"')) {
    return term.slice(1, -1).trim().toLocaleLowerCase()
  }
  return term.toLocaleLowerCase()
}

export function filterThreeDViewerParts (
  source: ThreeDViewerPartSearchSource[],
  query: string
): ThreeDViewerPartSearchResult[] {
  const term = getSearchTerm(query)
  if (!term) return []

  const results = new Map<string, ThreeDViewerPartSearchResult>()
  source.forEach((item) => {
    if (item.part.deleted === true) return

    const values = [
      item.part.partNumber,
      item.part.name,
      item.part.description,
      ...getTagValues(item.part.tags)
    ]
    const matches = values.some(value => String(value ?? '').toLocaleLowerCase().includes(term))
    if (!matches) return

    const key = getPartKey(item)
    const existing = results.get(key)
    results.set(key, {
      ...item,
      elementIds: [...new Set([...(existing?.elementIds ?? []), ...item.elementIds])],
      key,
      lockedAssemblyParentIds: {
        ...(existing?.lockedAssemblyParentIds ?? {}),
        ...(item.lockedAssemblyParentIds ?? {})
      }
    })
  })

  return [...results.values()]
}
